import { Box, CardProps, CircularProgress } from '@mui/material';
import { Add, ArrowBack, Check, CheckCircle, Close } from '@mui/icons-material';
import BaseCard from '@/components/ui/BaseCard';
import BaseButton from '@/components/ui/BaseButton';
import { useTranslation } from 'react-i18next';

interface ButtonGroupProps extends CardProps {
    isNew?: boolean;
    onBack?: () => void;
    onSave?: () => void;
    onSaveAs?: () => void;
    onCancel?: () => void;
    saveLoading?: boolean;
    saveDisabled?: boolean;
    saveAsDisabled?: boolean;
    saveAsLoading?: boolean;
    cancelDisabled?: boolean;
}

export default function ButtonGroup({
    isNew,
    onBack,
    onSave,
    saveDisabled = false,
    saveLoading = false,
    onSaveAs,
    saveAsDisabled = false,
    saveAsLoading = false,
    onCancel,
    cancelDisabled = false,
    ...props
}: ButtonGroupProps) {
    const { t } = useTranslation('common');
    return (
        <BaseCard sx={{ px: 2, py: 1.5, mb: 2 }} {...props}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    {onBack && (
                        <BaseButton
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                            }}
                            startIcon={<ArrowBack />}
                            variant="contained"
                            color="primary"
                            onClick={onBack}
                        >
                            {t('Back')}
                        </BaseButton>
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        width: { xs: '100%', sm: 'auto' },
                        mt: { xs: 1, sm: 0 },
                    }}
                >
                    {!isNew && onSave && (
                        <BaseButton
                            startIcon={<CheckCircle />}
                            variant="contained"
                            sx={{ backgroundColor: 'var(--confirm-btn-color)', width: { xs: '100%', sm: 'auto' } }}
                            onClick={onSave}
                            disabled={saveDisabled || saveLoading}
                        >
                            {saveLoading ? <CircularProgress size={20} /> : t('Save')}
                        </BaseButton>
                    )}
                    {onSaveAs && (
                        <BaseButton
                            startIcon={isNew ? <Add /> : <Check />}
                            variant="contained"
                            sx={{
                                backgroundColor: 'var(--success-color)',
                                width: { xs: '100%', sm: 'auto' },
                            }}
                            onClick={onSaveAs}
                            disabled={saveAsDisabled || saveAsLoading}
                        >
                            {saveAsLoading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <>
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                        {isNew ? t('Create') : t('Save As')}
                                    </Box>
                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                                        {isNew ? t('Create') : t('Save As')}
                                    </Box>
                                </>
                            )}
                        </BaseButton>
                    )}
                    {onCancel && (
                        <BaseButton
                            startIcon={<Close />}
                            variant="outlined"
                            disabled={cancelDisabled}
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                                borderColor: 'var(--cancel-btn-color)',
                                color: 'var(--cancel-btn-color)',
                                fontWeight: 'bold',
                            }}
                            onClick={onCancel}
                        >
                            {t('Cancel')}
                        </BaseButton>
                    )}
                </Box>
            </Box>
        </BaseCard>
    );
}
